/**
 * WhatsAppThreadPanel — panel izquierdo de Producción: el hilo de WhatsApp.
 *
 * Decisiones de diseño:
 * - El operador NUNCA tiene la caja física: este hilo ES la fuente de verdad
 *   (principio 3). Por eso ocupa el 40% fijo del split y nunca se colapsa.
 * - Mensajes del cliente a la izquierda (burbuja neutra con borde), del agente
 *   a la derecha (azul suave) — la convención visual de cualquier chat.
 * - Cada mensaje ofrece "Usar como fuente" al hover Y con foco de teclado
 *   (focus-within): la cita exacta del cliente viaja al buffer de Produccion.
 * - Búsqueda dentro del hilo insensible a acentos, con contador de coincidencias
 *   — encontrar "¿cuánto vale?" en 80 mensajes no puede tomar más que segundos.
 * - Multimedia sin red: las fotos del mock no resuelven, así que se muestra un
 *   marco de proporción fija con ícono y "Foto adjunta"; las notas de voz son
 *   un stub con duración (no hay reproducción real en esta fase).
 * - Salto de procedencia: cuando jumpToMessageId cambia, scrollIntoView + anillo
 *   ámbar temporal, y se notifica onJumpHandled para limpiar el estado arriba.
 * - Los mensajes que ya son fuente de artículos llevan un marcador Link2
 *   permanente: el operador ve de un vistazo qué partes del hilo ya se usaron.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, Image as ImageIcon, Link2 } from 'lucide-react';
import QuoteClientButton from './shared/QuoteClientButton';

const normalizar = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const horaCorta = (ts) => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const etiquetaDia = (ts) => {
  const d = new Date(ts);
  const hoy = new Date();
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);
  const mismo = (a, b) =>
    a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  if (mismo(d, hoy)) return 'Hoy';
  if (mismo(d, ayer)) return 'Ayer';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const WhatsAppThreadPanel = ({ thread = [], clientName, onQuote, jumpToMessageId, onJumpHandled }) => {
  const [consulta, setConsulta] = useState('');
  const [resaltadoId, setResaltadoId] = useState(null);
  const refsMensajes = useRef({});

  const mensajes = useMemo(() => {
    if (!consulta.trim()) return thread;
    const q = normalizar(consulta);
    return thread.filter((m) => normalizar(m.text).includes(q));
  }, [thread, consulta]);

  // Salto de procedencia: scroll + resaltado temporal.
  useEffect(() => {
    if (!jumpToMessageId) return undefined;
    setConsulta(''); // el mensaje destino debe estar visible
    const nodo = refsMensajes.current[jumpToMessageId];
    if (nodo) {
      nodo.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setResaltadoId(jumpToMessageId);
    }
    onJumpHandled && onJumpHandled();
    const t = setTimeout(() => setResaltadoId(null), 1800);
    return () => clearTimeout(t);
  }, [jumpToMessageId, onJumpHandled]);

  let diaAnterior = null;

  return (
    <div
      className="flex h-full min-h-0 flex-col rounded-lg border"
      style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
      aria-label={`Conversación de WhatsApp con ${clientName || 'el cliente'}`}
    >
      {/* ── Búsqueda en el hilo ── */}
      <div className="border-b p-2" style={{ borderColor: 'var(--border)', background: 'var(--tile)' }}>
        <input
          type="search"
          value={consulta}
          onChange={(e) => setConsulta(e.target.value)}
          placeholder="Buscar en la conversación…"
          aria-label="Buscar en la conversación"
          className="input w-full text-xs"
        />
        {consulta.trim() && (
          <p className="mt-1 text-[11px]" style={{ color: 'var(--text-secondary)' }} role="status">
            {mensajes.length} {mensajes.length === 1 ? 'mensaje coincide' : 'mensajes coinciden'}
          </p>
        )}
      </div>

      {/* ── Mensajes ── */}
      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-2.5">
        {mensajes.length === 0 && (
          <p className="px-2 py-4 text-center text-xs" style={{ color: 'var(--text-secondary)' }}>
            {consulta.trim()
              ? 'Ningún mensaje coincide con su búsqueda.'
              : 'La conversación con el cliente aparecerá aquí.'}
          </p>
        )}

        {mensajes.map((msg) => {
          const delCliente = msg.from === 'client';
          const dia = etiquetaDia(msg.timestamp);
          const mostrarDia = dia !== diaAnterior;
          diaAnterior = dia;
          const esFuente = Array.isArray(msg.is_source_for_items) && msg.is_source_for_items.length > 0;

          return (
            <div key={msg.id}>
              {mostrarDia && (
                <p
                  className="my-2 text-center text-[10px] font-medium uppercase tracking-wide"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  {dia}
                </p>
              )}
              <div
                ref={(el) => {
                  refsMensajes.current[msg.id] = el;
                }}
                className={`group flex ${delCliente ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`relative max-w-[85%] rounded-lg border px-2.5 py-1.5 transition-shadow ${
                    resaltadoId === msg.id ? 'ring-2' : ''
                  }`}
                  style={{
                    background: delCliente ? 'var(--tile)' : 'var(--blue-soft)',
                    borderColor: delCliente ? 'var(--border)' : 'var(--blue)',
                    '--tw-ring-color': 'var(--amber)',
                  }}
                >
                  {/* Multimedia */}
                  {msg.media_type === 'image' && (
                    <div
                      className="mb-1.5 flex aspect-[4/3] w-44 flex-col items-center justify-center gap-1 rounded-md border"
                      style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
                      role="img"
                      aria-label="Foto adjunta del cliente"
                    >
                      <ImageIcon size={20} aria-hidden="true" style={{ color: 'var(--text-tertiary)' }} />
                      <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                        Foto adjunta
                      </span>
                    </div>
                  )}
                  {msg.media_type === 'audio' && (
                    <span
                      className="mb-1 flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px]"
                      style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
                      aria-label={`Nota de voz de ${msg.duration_seconds || 0} segundos`}
                    >
                      <Mic size={12} aria-hidden="true" />
                      Nota de voz · {msg.duration_seconds || 0}s
                    </span>
                  )}

                  {msg.text && (
                    <p className="whitespace-pre-wrap text-xs" style={{ color: 'var(--text-main)' }}>
                      {msg.text}
                    </p>
                  )}

                  <span className="mt-0.5 flex items-center justify-end gap-1">
                    {esFuente && (
                      <Link2
                        size={10}
                        aria-hidden="true"
                        style={{ color: 'var(--blue-text)' }}
                        title="Este mensaje ya es fuente de artículos"
                      />
                    )}
                    <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                      {horaCorta(msg.timestamp)}
                    </span>
                  </span>

                  {/* Usar como fuente — visible al hover y con foco de teclado */}
                  <span
                    className="absolute -right-1 -top-1 opacity-0 transition-opacity
                               focus-within:opacity-100 group-hover:opacity-100"
                  >
                    <QuoteClientButton onQuote={() => onQuote({ messageId: msg.id, text: msg.text || '' })} />
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WhatsAppThreadPanel;

/*
Ejemplo de uso (en Produccion):
  <WhatsAppThreadPanel
    thread={draft.whatsapp_thread}
    clientName={draft.client?.name}
    onQuote={(cita) => setQuoteBuffer(cita)}
    jumpToMessageId={jumpToMessageId}
    onJumpHandled={() => setJumpToMessageId(null)}
  />
*/
